import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const CARD =
  "w-full max-w-md border border-white/10 bg-card p-10";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-6" data-testid="forgot-password-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className={CARD}
      >
        <p className="overline-label mb-4">Recupero accesso</p>
        <h1 className="font-serif font-light text-3xl mb-4">Password dimenticata?</h1>
        {sent ? (
          <div data-testid="forgot-success">
            <p className="text-muted-foreground text-sm leading-relaxed mb-8">
              Se l'email è registrata, riceverai a breve un link per reimpostare la password
              (valido 1 ora). Controlla anche la cartella spam.
            </p>
            <Link to="/auth" className="text-primary text-sm tracking-wide" data-testid="forgot-back-link">
              Torna al login
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6" data-testid="forgot-form">
            <p className="text-muted-foreground text-sm leading-relaxed">
              Inserisci la tua email: ti invieremo un link per impostare una nuova password.
            </p>
            <div className="space-y-2">
              <Label htmlFor="fp-email" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Email</Label>
              <Input
                id="fp-email"
                data-testid="forgot-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-secondary border-white/10 focus-visible:ring-primary"
              />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="forgot-error">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              data-testid="forgot-submit"
              className="w-full h-13 py-4 rounded-full bg-primary text-primary-foreground text-xs uppercase tracking-[0.25em] font-medium hover:bg-[#c8ad8f] transition-colors duration-300 disabled:opacity-50"
            >
              {busy ? "Invio…" : "Invia link di reset"}
            </button>
          </form>
        )}
      </motion.div>
    </main>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Le password non coincidono.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password aggiornata");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-6" data-testid="reset-password-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className={CARD}
      >
        <p className="overline-label mb-4">Recupero accesso</p>
        <h1 className="font-serif font-light text-3xl mb-8">Imposta nuova password</h1>
        {done ? (
          <div data-testid="reset-success">
            <p className="text-muted-foreground text-sm leading-relaxed mb-8">
              Password aggiornata con successo. Ora puoi accedere con la nuova password.
            </p>
            <Link to="/auth" className="text-primary text-sm tracking-wide" data-testid="reset-login-link">
              Vai al login
            </Link>
          </div>
        ) : !token ? (
          <p className="text-muted-foreground text-sm" data-testid="reset-no-token">
            Link non valido. Richiedi un nuovo reset dalla pagina di login.
          </p>
        ) : (
          <form onSubmit={submit} className="space-y-6" data-testid="reset-form">
            <div className="space-y-2">
              <Label htmlFor="rp-pass" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Nuova password</Label>
              <Input
                id="rp-pass"
                data-testid="reset-password-input"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-secondary border-white/10 focus-visible:ring-primary"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rp-confirm" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Conferma password</Label>
              <Input
                id="rp-confirm"
                data-testid="reset-confirm-input"
                type="password"
                required
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="bg-secondary border-white/10 focus-visible:ring-primary"
              />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              data-testid="reset-submit"
              className="w-full h-13 py-4 rounded-full bg-primary text-primary-foreground text-xs uppercase tracking-[0.25em] font-medium hover:bg-[#c8ad8f] transition-colors duration-300 disabled:opacity-50"
            >
              {busy ? "Salvataggio…" : "Salva nuova password"}
            </button>
          </form>
        )}
      </motion.div>
    </main>
  );
}
